import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { LEARNING_HUB_LANGUAGES, LEARNING_HUB_MODULE_PATH } from '../../definitions/learning-hub.constants';
import {
  LearningHubArticleMeta,
  LearningHubBreadcrumb,
  LearningHubCategories,
  LearningHubCategory,
  LearningHubIndex,
  LearningHubLanguage,
  LearningHubLanguageOption,
  LearningHubTag
} from '../../definitions/learning-hub.models';
import { LearningHubService } from '../../services/learning-hub.service';
import { filterArticles, resolveLanguage } from '../../utils/learning-hub.utils';

interface ListData {
  state: ListState;
  articles: LearningHubArticleMeta[];
  tree: LearningHubCategories;
}

interface ListState {
  lang: LearningHubLanguage;
  query: string;
  tag: string | null;
}

/** Search results: /learning-hub/search?lang=..&q=..&tag=.. (old /tag/<tag>/ URLs land here too). */
@Component({
  selector: 'app-learning-hub-article-list',
  templateUrl: './article-list.component.html',
  styleUrls: ['./article-list.component.scss']
})
export class ArticleListComponent implements OnInit, OnDestroy {
  readonly modulePath = LEARNING_HUB_MODULE_PATH;
  readonly languageOptions: LearningHubLanguageOption[] = LEARNING_HUB_LANGUAGES.map((lang: LearningHubLanguage) => ({
    lang,
    available: true
  }));

  lang: LearningHubLanguage;
  query = '';
  tree: LearningHubCategories;
  articles: LearningHubArticleMeta[] = [];
  results: LearningHubArticleMeta[] = [];
  selectedTag: LearningHubTag | null = null;
  breadcrumb: LearningHubBreadcrumb[] = [];
  loading = true;
  error = false;

  private readonly DESTROY$ = new Subject<void>();

  constructor(
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _translate: TranslateService,
    private readonly _learningHubService: LearningHubService
  ) {}

  ngOnInit(): void {
    this._route.queryParamMap
      .pipe(
        map((params: ParamMap) => this._toState(params)),
        tap((state: ListState) => {
          this.lang = state.lang;
          this.query = state.query;
          this.loading = true;
          this.error = false;
          this.breadcrumb = [
            { label: 'learningHub.title', translate: true, link: ['/', this.modulePath], queryParams: { lang: state.lang } },
            { label: 'learningHub.searchTitle', translate: true }
          ];
        }),
        switchMap((state: ListState) =>
          combineLatest([this._learningHubService.getIndex(state.lang), this._learningHubService.getCategories()]).pipe(
            map(([index, tree]: [LearningHubIndex, LearningHubCategories]): ListData => ({
              state,
              articles: index.articles,
              tree
            })),
            catchError(() => of(null))
          )
        ),
        takeUntil(this.DESTROY$)
      )
      .subscribe((data: ListData | null) => {
        this.loading = false;
        if (!data) {
          this.error = true;
          this.articles = [];
          this.results = [];
          return;
        }
        this.tree = data.tree;
        this.articles = data.articles;
        this.selectedTag = this._findTag(data.articles, data.state.tag);
        this.results = filterArticles(
          data.articles,
          { query: data.state.query, tag: this.selectedTag?.slug },
          this._categoryLabels(data.tree.categories, data.state.lang)
        );
      });
  }

  ngOnDestroy(): void {
    this.DESTROY$.next();
    this.DESTROY$.complete();
  }

  search(query: string): void {
    this._updateQueryParams({ q: query || null }, true);
  }

  changeLanguage(option: LearningHubLanguageOption): void {
    // Tags are language specific in WordPress, so the tag filter is dropped when switching languages.
    this._updateQueryParams({ lang: option.lang, tag: null });
  }

  clearTag(): void {
    this._updateQueryParams({ tag: null });
  }

  clearFilters(): void {
    this._updateQueryParams({ q: null, tag: null });
  }

  private _toState(params: ParamMap): ListState {
    return {
      lang: resolveLanguage(params.get('lang'), this._translate.currentLang, this._translate.defaultLang),
      query: params.get('q') || '',
      tag: params.get('tag')
    };
  }

  private _findTag(articles: LearningHubArticleMeta[], slug: string | null): LearningHubTag | null {
    if (!slug) {
      return null;
    }
    for (const article of articles) {
      const tag = article.tags.find((item: LearningHubTag) => item.slug === slug);
      if (tag) {
        return tag;
      }
    }
    return { slug, name: slug };
  }

  /** WordPress label of every category in this language plus its i18n label, so both can be searched. */
  private _categoryLabels(categories: LearningHubCategory[], lang: LearningHubLanguage): Record<string, string> {
    return categories.reduce((labels: Record<string, string>, category: LearningHubCategory) => {
      const key = `learningHub.categories.${category.key}`;
      const translated = this._translate.instant(key);
      labels[category.key] = [category.labels[lang], translated !== key ? translated : ''].filter(Boolean).join(' ');
      return labels;
    }, {});
  }

  private _updateQueryParams(queryParams: Record<string, string | null>, replaceUrl = false): void {
    this._router.navigate([], { relativeTo: this._route, queryParams, queryParamsHandling: 'merge', replaceUrl });
  }
}
